import { DateTime } from "luxon";
import { BuildServices, GenericRouteEvent, RouteEventAction, RouteEventActionColorMap, RouteEventActionFillMap, RouteEventActionTypeMap } from "./generic-route-event.model";
import { Route } from "src/app/types/route.type";
import * as helpers from '../../helpers/ui-helpers';
import { BaseJob } from "../job.type";
import { Capacitor } from "@capacitor/core";

export class PullIn extends GenericRouteEvent {

  public type = 'pull-in';

  earliestStartTime: DateTime;
  value: BaseJob;

  constructor(route: Route, job: BaseJob, services: BuildServices) {
    super(services);
    const { scheduledEnd, pullInEarlinessLimit: limit } = route;
    this.completed = !!job.arriveTime;
    this.scheduledStart = DateTime.fromISO(job.scheduledTime ?? route.scheduledEnd?.toDateString() ?? job.estimatedTimeOfArrival);
    this.value = job;
    this.stopSequence = job.sequence;
    this.headerInfo = {
      time: helpers.getTimeForCardHeader({ start: this.scheduledStart, end: this.scheduledEnd, translateSvc: services.translateSvc }),
      type: 'LABEL.pullIn',
      details: '',
      timeTo: this.scheduledStart.toRelative(),
    };
    this.earliestStartTime = this.scheduledStart.minus({ seconds: limit ?? 0 });
    this.location = job.location;

    this.updateTimeBasedProperties();
  }

  updateTimeBasedProperties = () => {
    const timeToDiff = this.started ? this.scheduledStart : this.scheduledEnd;
    this.isLate = this.completed ? false : helpers.isInThePast(timeToDiff);
    const actions = this.getActions();
    if (JSON.stringify(actions) !== JSON.stringify(this.actions)) {
      this.actions = this.getActions();
    }
    this.headerInfo.timeTo = this.scheduledStart?.toRelative({ unit: [ 'hours', 'minutes'  ], padding: 6000 });
  };

  public getActions(): RouteEventAction[] {
    if (!helpers.isInThePast(this.earliestStartTime)) { return []; }
    const actions: RouteEventAction[] = [
      {
        show: !this.completed,
        type: RouteEventActionTypeMap.perform,
        label: this.translateSvc.translate('LABEL.pullIn'),
        icon: '',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.perform(action),
      },
      {
        type: RouteEventActionTypeMap.navigate,
        label: `ACTION.navigate`,
        show: this.value.jobEnabled && helpers.hasValidGeo(this.location),
        icon: 'navigate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: true,
        disabled: false,
        fn: async () => this.locationSvc.navigateToDestination(this.location.coordinate),
      },
      {
        type: RouteEventActionTypeMap.seeOn,
        label: `${ this.translateSvc.translate('LABEL.seeOn') } ${ this.translateSvc.translate('LABEL.map') }`,
        show: (this.appData.currentRouteIsPreview || Capacitor.getPlatform() !== 'ios') && helpers.hasValidGeo(this.location),
        icon: 'map', color: RouteEventActionColorMap.medium,
        fill: RouteEventActionFillMap.outline,
        preview: true,
        drivingMode: false,
        disabled: false,
        fn: async () => this.locationSvc.previewLocation(this.location.coordinate, this.location.name),
      },
    ].filter(action => action.show);
    return actions;
  }

  async perform(action: RouteEventAction): Promise<void> {
    try {
      action.disabled = true;
      const confirmedOdo = await helpers.presentOdometer(this.modalController);
      if (!confirmedOdo) { return; }
      this.completed = true;
      this.value.arriveTime = new Date().toString();
      await this.dispatchSvc.arriveJobs([ this ]);
      this.value.completed = true;
      this.appData.setJobs([ this.value ]);
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      action.disabled = false;
    }
  }
}
